import { LineSeg2d } from '../../math/LineSeg2d';
import { IPoint2d } from '../../math/IPoint2d';
import { equalsNumber, isClockwiseWithArc, normalizeAngle } from '../../math/utils';
import { getPointOnSeg, getPointPosOnSeg, getShapeSeg, isTangentShape } from '@/editor/math/shape/other';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { RTree, RTreeNode } from '@/editor/utils/RTree';
import { isMatch, isNumber } from 'lodash-es';
import { EClosedPathRelation, IShapePoint2d, IShapeSeg2d } from '@/editor/types/shape';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { sortPointsIndexes } from '@/editor/math/shape/sort';
import { getPathFromEdges } from '@/editor/utils/edge';
import { calculateShapeRelation } from '@/editor/math/shape/path';
import { arcThresholdValue } from '@/editor/math/const';
import { dispersePath } from '@/editor/utils/shape';
import { calculatePolygonArea } from './utils';

/** 点是否落在线段内部（在线段上且不是两端点） */
function isPointInsideSeg(seg: LineSeg2d, point: IPoint2d): boolean {
  if (Point2dUtils.equals(point, seg.start) || Point2dUtils.equals(point, seg.end)) return false;
  return seg.pointOnSegment(point);
}

interface IGeometryEdge {
  from: IPoint2d;
  to: IPoint2d;
  center?: IPoint2d;
  clockwise?: boolean;
  angle: [number, number];
}

export interface Edge<T = null> extends IGeometryEdge {
  info: T;
}

// 几何相切判据（带容差，对半径/坐标的浮点噪声鲁棒）：
// 弧-弧：圆心距 ≈ 半径和（外切）或 ≈ 半径差（内切）；弧-直线：圆心到直线距离 ≈ 半径。
// 解决「同一圆被拆成两段、半径有 ~1e-5 误差」导致本应相切却被求交算成偏移 ~0.5mm 的假交点问题。
function nearTangent(segI: IShapeSeg2d, segJ: IShapeSeg2d): boolean {
  const tol = arcThresholdValue;
  const arcI = segI instanceof ArcSeg2d ? segI : null;
  const arcJ = segJ instanceof ArcSeg2d ? segJ : null;
  if (arcI && arcJ) {
    const d = Point2dUtils.distanceTo(arcI.center, arcJ.center);
    return Math.abs(d - (arcI.radius + arcJ.radius)) < tol || Math.abs(d - Math.abs(arcI.radius - arcJ.radius)) < tol;
  }
  const arc = arcI || arcJ;
  const line = arcI ? segJ : segI;
  if (!arc) return false;
  const ax = line.start.x;
  const ay = line.start.y;
  const bx = line.end.x - ax;
  const by = line.end.y - ay;
  const L = Math.hypot(bx, by);
  if (L < 1e-9) return false;
  const dist = Math.abs((arc.center.x - ax) * by - (arc.center.y - ay) * bx) / L;
  return Math.abs(dist - arc.radius) < tol;
}

// 计算两条相切 seg 的精确切点（near 为求交得到的近似切点，用于在多候选中取最近者）。
// 弧-弧：切点在两圆心连线上，距圆心 i 为 r 处（内/外切对应连线两侧，取更接近 near 的候选）。
// 弧-直线：切点为圆心到直线的垂足。返回 null 表示无法计算（退回使用近似交点）。
function tangentPoint(segI: IShapeSeg2d, segJ: IShapeSeg2d, near: IPoint2d): IPoint2d | null {
  const arcI = segI instanceof ArcSeg2d ? segI : null;
  const arcJ = segJ instanceof ArcSeg2d ? segJ : null;

  if (arcI && arcJ) {
    const d = Point2dUtils.distanceTo(arcI.center, arcJ.center);
    if (d < 1e-9) return null;
    const ux = (arcJ.center.x - arcI.center.x) / d;
    const uy = (arcJ.center.y - arcI.center.y) / d;
    const cand1 = { x: arcI.center.x + arcI.radius * ux, y: arcI.center.y + arcI.radius * uy };
    const cand2 = { x: arcI.center.x - arcI.radius * ux, y: arcI.center.y - arcI.radius * uy };
    return Point2dUtils.distanceTo(cand1, near) <= Point2dUtils.distanceTo(cand2, near) ? cand1 : cand2;
  }

  const arc = arcI || arcJ;
  const line = arcI ? segJ : segI;
  if (!arc) return null;
  // 圆心在直线上的垂足
  const ax = line.start.x;
  const ay = line.start.y;
  const bx = line.end.x - ax;
  const by = line.end.y - ay;
  const len2 = bx * bx + by * by;
  if (len2 < 1e-9) return null;
  const t = ((arc.center.x - ax) * bx + (arc.center.y - ay) * by) / len2;
  return { x: ax + t * bx, y: ay + t * by };
}

// 在某点沿给定出发切线方向的带符号曲率（只依赖 center 与起点，便于给「虚拟反向边」复用）。
// 直线/无圆心曲率为 0；圆弧：圆心在出发方向左侧(逆时针弯)为正、右侧为负，绝对值为 1/半径。
// 用于在顶点处对「相切（出发切线相同）」的边做稳定排序破平：曲率越大越偏逆时针。
function signedCurvatureAt(center: IPoint2d | undefined, from: IPoint2d, departureAngle: number): number {
  if (!center) return 0;
  const cx = center.x - from.x;
  const cy = center.y - from.y;
  const radius = Math.hypot(cx, cy);
  if (radius === 0) return 0;
  const cross = Math.cos(departureAngle) * cy - Math.sin(departureAngle) * cx;
  return (cross > 0 ? 1 : -1) / radius;
}

// 边在其 from 端、沿行进方向的带符号曲率
function edgeSignedCurvature(edge: IGeometryEdge): number {
  return signedCurvatureAt(edge.center, edge.from, edge.angle[0]);
}

// 判断 e 是否为 a 的「精确反向半边」（同一物理几何、首尾互换）：直线首尾互换；
// 圆弧首尾互换 + 同圆心 + clockwise 取反（以区分同圆心的另一段半圆）。用于面遍历中识别原路折返边。
function isReverseTwinEdge(a: IGeometryEdge, e: IGeometryEdge): boolean {
  if (!Point2dUtils.equals(a.from, e.to) || !Point2dUtils.equals(a.to, e.from)) return false;
  // 一弧一直线不成对；两条都是直线时，首尾互换即可判定
  if (!a.center || !e.center) return !a.center && !e.center;
  return Point2dUtils.equals(a.center, e.center) && a.clockwise === !e.clockwise;
}

// 消去环中相邻的「反向 twin 对」（含首尾环绕）：这类一出一回的边是悬挂刺/只起连接作用、
// 并非区域边界（面积贡献为 0），应从轮廓中剔除。反复消去直到稳定，剩下干净的边界环。
function removeSpurs<T>(cycle: Edge<T>[]): Edge<T>[] {
  const edges = [...cycle];
  let changed = true;
  while (changed && edges.length >= 2) {
    changed = false;
    for (let i = 0; i < edges.length; i++) {
      const j = (i + 1) % edges.length;
      if (i === j) break;
      if (isReverseTwinEdge(edges[i], edges[j])) {
        // 删除 i、j 两条（j 可能因环绕在 i 之前）
        const hi = Math.max(i, j);
        const lo = Math.min(i, j);
        edges.splice(hi, 1);
        edges.splice(lo, 1);
        changed = true;
        break;
      }
    }
  }
  return edges;
}

interface IAreaInfo<T = null> {
  isHole?: boolean;
  edges: Edge<T>[];
  path: IShapePoint2d[];
  clockwise: boolean;
  inside: IAreaInfo<T>[];
  outside: IAreaInfo<T>[];
  parent: IAreaInfo<T> | null;
  children: IAreaInfo<T>[];
  /** 该环生成的区域结果，只有逆时针环（房间轮廓）才会写入。回溯祖先时用它把子区域挂到最近的已生成区域上 */
  area?: IAreaResult<T>;
}

export interface IAreaResult<T = null> {
  edges: Edge<T>[];
  path: IPoint2d[];
  holes: IPoint2d[][];
  holesEdges: Edge<T>[][];
  parent: IAreaResult<T> | null;
  children: IAreaResult<T>[];
}

interface IPathFinderParams<T = null> {
  segments: IShapeSeg2d[];
  segRelativeInfos?: T[];
  isDirectedSegments?: boolean;
  needClearFreeEdges?: boolean;
  preserveOppositeEdge?: boolean;
}

export class PathFinder<T = null> {
  private _segments: IShapeSeg2d[] = [];
  private _isDirectedSegments: boolean = true; // 是否需要保留segments中的方向
  private _segRelativeInfos: T[] | null = null; // 和segments对应的墙体信息
  private _needClearFreeEdges: boolean = false; // 是否需要清除自由边
  private _preserveOppositeEdge: boolean = false; // 是否需要保留反向边

  private _edges: Edge<T>[] = [];
  private _oppositeEdges: Edge<T>[] = []; // 反向边记录
  private _pointTree = new RTree<T>(); // 用来做邻近点合并，端点需要从这里取
  private _graph: Map<IPoint2d, Edge<T>[]> = new Map();

  constructor(params?: IPathFinderParams<T>) {
    if (params) {
      this.setParams(params);
    }
  }

  setParams({
    segments,
    segRelativeInfos,
    isDirectedSegments,
    needClearFreeEdges,
    preserveOppositeEdge
  }: IPathFinderParams<T>) {
    this.clear();

    this._segments = segments; // 后面会替换start、end
    this._segRelativeInfos = segRelativeInfos || null;

    this._isDirectedSegments = typeof isDirectedSegments === 'boolean' ? isDirectedSegments : true;
    this._needClearFreeEdges = typeof needClearFreeEdges === 'boolean' ? needClearFreeEdges : false;
    this._preserveOppositeEdge = typeof preserveOppositeEdge === 'boolean' ? preserveOppositeEdge : false;

    this.init();
  }

  // 往pointTree中添加点，会对info进行去重
  private addPoint(point: IPoint2d, info?: T | null) {
    if (!info) return this._pointTree.addPoint(point);

    const existNode = this._pointTree.searchNearNode(point);
    if (!existNode) return this._pointTree.addPoint(point, info);

    const infos = existNode.infos;
    if (!infos.find((item) => isMatch(item as object, info as object))) infos.push(info);
    return existNode;
  }

  getNode(point: IPoint2d) {
    return this._pointTree.searchNearNode(point);
  }

  // 处理线段，计算交点并分割
  private processSegments() {
    const intersectMap: Map<IShapeSeg2d, RTreeNode<T>[]> = new Map(); // 记录线段上的交点
    const segments = this._segments;
    const edges: Edge<T>[] = [];

    // 1. 添加所有线段端点
    const len = segments.length;
    for (let i = 0; i < len; i++) {
      const info = this._segRelativeInfos ? this._segRelativeInfos[i] : null;
      const startNode = this.addPoint(segments[i].start, info);
      const endNode = this.addPoint(segments[i].end, info);
      intersectMap.set(segments[i], [startNode, endNode]);
    }

    let allIntersects: IPoint2d[] = [];
    // 2. 计算所有线段交点，并添加到检索树中
    for (let i = 0; i < len; i++) {
      for (let j = i + 1; j < len; j++) {
        let intersects = intersectCommonShape(segments[i], segments[j]);
        if (!intersects) continue;

        const iIsArc = segments[i] instanceof ArcSeg2d;
        const jIsArc = segments[j] instanceof ArcSeg2d;

        // 同圆心的两段弧不会有交点
        if (
          iIsArc &&
          jIsArc &&
          Point2dUtils.distanceTo((segments[i] as ArcSeg2d).center, (segments[j] as ArcSeg2d).center) <
            arcThresholdValue
        ) {
          continue;
        }

        // 相切（弧-弧 或 弧-直线）：相切只有一个切点，但求交在相切处数值不稳，可能返回有较大误差或落在弧外的点。
        // 直接用几何方式算精确切点：弧-弧取圆心连线上距圆心 r1 的候选点；弧-直线取圆心到直线的垂足。
        // 切点可能落在端点（如相切于公共端点），也可能在弧中间（如内切于非端点处 / 圆与边相切于角点），都能正确得到。
        if (
          (iIsArc || jIsArc) &&
          intersects.length > 0 &&
          (isTangentShape(segments[i], segments[j]) || nearTangent(segments[i], segments[j]))
        ) {
          const tp = tangentPoint(segments[i], segments[j], intersects[0]);
          if (tp) intersects = [tp];
        }

        // 过滤掉不落在两条 seg 上的假交点：近相切的圆/弧求交数值不稳，可能返回落在弧外（圆外或角度区间外）
        // 的错误点（例如把另一段弧的端点误当成交点），若直接用作分裂点会切出跨越式的错误大段。
        intersects = intersects.filter(
          (pt) => getPointPosOnSeg(segments[i], pt) !== null && getPointPosOnSeg(segments[j], pt) !== null
        );
        if (intersects.length === 0) continue;

        // 将一些所求的有误差的交点改为现有的交点值，解决误差问题
        for (let k = 0; k < intersects.length; k++) {
          for (const allIntersect of allIntersects) {
            if (Point2dUtils.equals(intersects[k], allIntersect, arcThresholdValue)) {
              intersects[k] = allIntersect;
              break;
            }
          }
        }
        allIntersects = allIntersects.concat(intersects);

        const nodes = intersects.map((intersect, index) => {
          let node: RTreeNode<T>;
          if (this._segRelativeInfos) {
            // TODO: 仅在多交点时附加 index 以区分同一线段对的不同交点以兼容之前的结构，后续需要优化
            const extra = intersects.length > 1 ? { index } : {};
            this.addPoint(intersect, { ...this._segRelativeInfos[i], ...extra });
            node = this.addPoint(intersect, { ...this._segRelativeInfos[j], ...extra });
          } else {
            node = this._pointTree.addPoint(intersect);
          }

          return node;
        });

        intersectMap.set(segments[i], [...(intersectMap.get(segments[i]) || []), ...nodes]);
        intersectMap.set(segments[j], [...(intersectMap.get(segments[j]) || []), ...nodes]);
      }
    }

    // 2.5 共线重叠线段的分割点
    // 共线重叠的两条线段没有唯一交点，intersectCommonShape 返回 null，上面那轮拿不到任何分割点。
    // 但重叠段的端点是真实的 T 形分割点：例如隔墙从厚墙中线起步向外走，起步那一小段与厚墙的
    // 端盖共线重叠，端盖就必须在隔墙起点处断开。漏掉的话该节点上会出现两条方向完全相同的出边，
    // 其中一条是走不通的断头，环路追踪会卡在这里，整个区域丢失。
    for (let i = 0; i < len; i++) {
      const segI = segments[i];
      if (!(segI instanceof LineSeg2d)) continue;

      for (let j = i + 1; j < len; j++) {
        const segJ = segments[j];
        if (!(segJ instanceof LineSeg2d)) continue;
        if (!segI.isCollinear(segJ)) continue;

        const overlap = segI.getOverlapSegment(segJ);
        if (!overlap) continue;

        for (const point of [overlap.start, overlap.end]) {
          // 只取真正落在某条线段内部的点。完全重合的反向边对（隔墙的两侧轮廓）重叠段端点就是
          // 各自的端点，取不到内部点，这里不会产生任何改动
          const insideI = isPointInsideSeg(segI, point);
          const insideJ = isPointInsideSeg(segJ, point);
          if (!insideI && !insideJ) continue;

          // 不带 info，避免给已有交点混入额外的墙体信息
          const node = this.addPoint(point);
          if (insideI) intersectMap.set(segI, [...(intersectMap.get(segI) || []), node]);
          if (insideJ) intersectMap.set(segJ, [...(intersectMap.get(segJ) || []), node]);
        }
      }
    }

    // 3. 分割所有线段
    const segLen = segments.length;
    for (let segIndex = 0; segIndex < segLen; segIndex++) {
      const seg = segments[segIndex];

      // 把起点、交点、终点拼接起来
      let nodesOnSeg = intersectMap.get(seg) || [];

      // 排序
      nodesOnSeg = sortPointsIndexes(
        nodesOnSeg.map((node) => ({ x: node.point.x, y: node.point.y, node })),
        seg
      ).map((index) => nodesOnSeg[index]);

      // 点去重
      for (let i = nodesOnSeg.length - 1; i > 0; i--) {
        if (nodesOnSeg[i] === nodesOnSeg[i - 1]) {
          nodesOnSeg.splice(i, 1);
        }
      }

      if (nodesOnSeg.length < 2) continue;

      // 创建分割后的边
      const pLen = nodesOnSeg.length;
      for (let i = 0; i < pLen - 1; i++) {
        const from = nodesOnSeg[i].point;
        const to = nodesOnSeg[i + 1].point;
        const subSeg =
          seg instanceof ArcSeg2d
            ? new ArcSeg2d({ start: from, end: to, center: seg.center, clockwise: seg.clockwise })
            : new LineSeg2d(from, to);

        const angle = getTangentAngle(subSeg);
        const info = this._segRelativeInfos ? this._segRelativeInfos[segIndex] : null;
        let currentEdge =
          seg instanceof ArcSeg2d
            ? { from, to, angle, info: info as T, center: seg.center, clockwise: seg.clockwise }
            : { from, to, angle, info: info as T };

        // 由于之前的误差计算，可能会有些点做了优化，这里的判断是为了排查掉不在的弧边界使用了直线做处理。
        // 判据用「弦长 > 直径」：圆上任意两点的弦长最大为直径(2r)，超过说明端点已被优化到圆外、该子弧不成立。
        // 注意不能用「弦长 > 半径」：圆心角 >60° 的合法弧其弦长必然 > 半径(弦=2r·sin(θ/2))，会误伤大跨度圆弧。
        if (
          seg instanceof ArcSeg2d &&
          Point2dUtils.distanceTo(from, to) > 2 * seg.radius &&
          Point2dUtils.distanceTo(seg.start, subSeg.start) > seg.radius &&
          Point2dUtils.distanceTo(seg.end, subSeg.end) > seg.radius
        ) {
          currentEdge = { from, to, angle, info: info as T };
        }

        if (this._preserveOppositeEdge) {
          // 保留反向边
          edges.push(currentEdge);
        } else {
          // 不保留反向边
          const oppositeEdgeIndex = edges.findIndex((e) => isOppositeEdge(e, currentEdge));
          if (oppositeEdgeIndex !== -1 && !this._preserveOppositeEdge) {
            this._oppositeEdges.push(edges[oppositeEdgeIndex], currentEdge);
            edges.splice(oppositeEdgeIndex, 1); // 有可能会导致bug，待观察
          } else {
            edges.push(currentEdge);
          }
        }
      }
    }

    // edge去重
    if (!this._preserveOppositeEdge) {
      for (let i = edges.length - 1; i > 0; i--) {
        const e1 = edges[i];
        const e2 = edges[i - 1];

        if (e1.center && !e2.center) continue;
        if (!e1.center && e2.center) continue;
        if (e1.center && e2.center) {
          if (!Point2dUtils.equals(e1.center, e2.center)) continue;
          if (e1.clockwise === e2.clockwise) continue;
        }

        if (Point2dUtils.equals(e1.from, e2.to) && Point2dUtils.equals(e1.to, e2.from)) edges.splice(i, 1);
      }
    }

    this._edges = edges;
  }

  // 构建邻接表图结构
  private buildGraph() {
    const edges = this._edges;
    const graph = this._graph;

    // 添加边到邻接表
    for (const edge of edges) {
      let fromEdges = graph.get(edge.from);
      if (!fromEdges) {
        fromEdges = [];
        graph.set(edge.from, fromEdges);
      }
      fromEdges.push(edge);
    }

    // 清除自由边
    if (this._needClearFreeEdges) {
      this.clearFreeEdges();
    }

    if (!this._isDirectedSegments) {
      for (const edges of graph.values()) {
        edges.forEach((e) => {
          const oppositeEdge = {
            from: e.to,
            to: e.from,
            angle: e.angle.map((angle) => normalizeAngle(angle + Math.PI)) as [number, number],
            info: e.info
          };

          let oppositeFromEdges = graph.get(oppositeEdge.from);
          if (!oppositeFromEdges) {
            oppositeFromEdges = [];
            graph.set(oppositeEdge.from, oppositeFromEdges);
          }
          oppositeFromEdges.push(oppositeEdge);
        });
      }
    }

    // 对每个点的邻接边按角度排序(逆时针)
    for (const edges of graph.values()) {
      edges.sort((a, b) => a.angle[0] - b.angle[0]);
    }
  }

  private clearFreeEdges() {
    while (true) {
      let loop = false;
      for (const edges of this._graph.values()) {
        for (let i = edges.length - 1; i > 0; i--) {
          const edge = edges[i];
          const toEdges = this._graph.get(edge.to);
          if (!toEdges?.length) {
            edges.splice(i, 1);
            loop = true;
          } else if (toEdges.length === 1) {
            // 如果to点的邻接边只有一条，则删除该边
            const edgesFromToPoint = this._graph.get(edge.to);
            if (edgesFromToPoint?.length === 1) {
              this._graph.delete(edge.to);
              edges.splice(i, 1);
              loop = true;
            }
          }
        }
      }

      if (!loop) break;
    }
  }

  private clear() {
    this._segments.length = 0;
    this._edges.length = 0;
    this._oppositeEdges.length = 0;
    this._segRelativeInfos = null;
    this._isDirectedSegments = true;
    this._needClearFreeEdges = false;
    this._preserveOppositeEdge = false;
    this._graph.clear();
    this._pointTree.clear();
  }

  // 数据初始化：将segments转换为edges、pointTree、graph
  private init() {
    if (this._segments.length === 0) return;

    // 1. 处理线段，计算交点并分割
    this.processSegments();

    // 2. 构建邻接表图结构
    this.buildGraph();
  }

  getPath(startPoint: IPoint2d, endPoint: IPoint2d): IShapePoint2d[] {
    // TODO: 待优化，多edge都匹配上的情况处理
    const startEdge = this._edges.find((edge) => {
      const seg = getShapeSeg(edge);
      const pos = getPointPosOnSeg(seg, startPoint);
      return isNumber(pos) && pos < 1;
    });
    const endEdge = this._edges.find((edge) => {
      const seg = getShapeSeg(edge);
      const pos = getPointPosOnSeg(seg, endPoint);
      return isNumber(pos) && pos > 0;
    });

    if (!startEdge || !endEdge) return [];

    // 同边处理
    if (startEdge === endEdge) {
      const seg = getShapeSeg(endEdge);
      const startPos = getPointPosOnSeg(seg, startPoint);
      const endPos = getPointPosOnSeg(seg, endPoint);

      if (!isNumber(startPos) || !isNumber(endPos) || startPos >= endPos) return [];

      // 起点与终点落在同一条边上：轮廓就是这条边上从 startPoint 到 endPoint 的那一小段，
      // 应返回被截断的查询点本身，而不是整条边的两个端点(seg.start / seg.end 可能在数千毫米外)。
      const points: IShapePoint2d[] = [
        { x: startPoint.x, y: startPoint.y },
        { x: endPoint.x, y: endPoint.y }
      ];
      if (seg instanceof ArcSeg2d) {
        points[0].center = seg.center;
        points[0].clockwise = seg.clockwise;
      }

      return points;
    }

    const pathEdges: Edge<T>[] = [];

    let i = 0; // 添加最大循环次数，防止死循环
    let currentEdge = startEdge;
    let reachedEnd = false;
    while (i < 100 && currentEdge) {
      if (currentEdge === endEdge) {
        reachedEnd = true;
        break;
      }
      i++;

      pathEdges.push(currentEdge);
      const nextEdges = this._graph.get(currentEdge.to);
      if (!nextEdges || nextEdges.length === 0) break;

      let closestEdge: Edge<T> | undefined;
      let maxAngle = -Infinity;
      const compareAngle = normalizeAngle(currentEdge.angle[1] + Math.PI);
      nextEdges.forEach((e) => {
        const angle = normalizeAngle(e.angle[0] - compareAngle);
        if (angle > maxAngle) {
          maxAngle = angle;
          closestEdge = e;
        }
      });

      // 所有出边的夹角都算不出来(NaN)时取不到下一条边，与上面「断头」一样判失败
      if (!closestEdge) break;
      currentEdge = closestEdge;
    }

    // 计算失败
    if (i === 100) return [];
    // 走到「悬空自由边」的尽头(该点没有出边)却还没走到终点边：图不连通，两段之间没有轮廓可走。
    // 此前无条件把 endEdge 接在断头后面，路径会从断头「跳」到终点边的起点(可能在数千毫米外的另一条边的远端)，
    // 拼出一条穿越空白的假轮廓。这种情况应判定为失败、由调用方退回其它端盖策略。
    if (!reachedEnd) return [];

    // 添加终点边
    pathEdges.push(endEdge);

    // 处理起点边，进行截断
    const startSeg = getShapeSeg(startEdge);
    const startPos = getPointPosOnSeg(startSeg, startPoint);
    const startSplitEdge = isNumber(startPos) ? splitEdge(startEdge, startPos, 1) : null;

    // 处理终点边，进行截断
    const endSeg = getShapeSeg(endEdge);
    const endPos = getPointPosOnSeg(endSeg, endPoint);
    const endSplitEdge = isNumber(endPos) ? splitEdge(endEdge, 0, endPos) : null;

    // 两条边是按「startPoint 在其上且 pos < 1」「endPoint 在其上且 pos > 0」筛出来的，正常截不空；
    // 真截空说明筛选与截断的判定对不上，按失败返回，不让 null 混进 pathEdges。
    if (!startSplitEdge || !endSplitEdge) return [];

    pathEdges[0] = startSplitEdge;
    pathEdges[pathEdges.length - 1] = endSplitEdge;

    const path = getPathFromEdges(pathEdges);
    return path;
  }

  getArea(): {
    areaResult: IAreaResult<T>[];
    outerAreas: IAreaResult<T>[];
    remainingEdges: Edge<T>[];
  } {
    const loops = this.getLoop();
    const areaInfos: IAreaInfo<T>[] = [];
    for (const edges of loops) {
      const path = getPathFromEdges(edges);
      const clockwise = isClockwiseWithArc(path);

      // 顺时针是墙体外边界，逆时针是房间轮廓
      areaInfos.push({
        edges,
        path,
        clockwise,
        inside: [],
        outside: [],
        parent: null,
        children: []
      });
    }

    // 计算出初始包含关系
    const areaLen = areaInfos.length;
    for (let i = 0; i < areaLen; i++) {
      for (let j = i + 1; j < areaLen; j++) {
        const area1 = areaInfos[i];
        const area2 = areaInfos[j];
        const loop1 = area1.path;
        const loop2 = area2.path;

        const relation = calculateShapeRelation(loop1, loop2);
        if (relation === EClosedPathRelation.Overlap) {
          // 重叠区域，顺逆时针分属外内环
          const outsideArea = area1.clockwise ? area1 : area2;
          const insideArea = area1.clockwise ? area2 : area1;
          outsideArea.inside.push(insideArea);
          insideArea.outside.push(outsideArea);
          continue;
        }

        if (relation === EClosedPathRelation.Inside) {
          area2.inside.push(area1);
          area1.outside.push(area2);
          continue;
        }

        if (relation === EClosedPathRelation.Outside) {
          area1.inside.push(area2);
          area2.outside.push(area1);
          continue;
        }
      }
    }

    // 计算出所有父子关系
    const roots = areaInfos.filter((info) => !info.outside.length);
    const relationQueue = [...roots];
    while (relationQueue.length) {
      const currentNode = relationQueue.pop();
      if (!currentNode) break;

      const children = (currentNode?.inside || []).filter((descendantNode) => {
        const currentIndex = descendantNode.outside.findIndex((node) => node === currentNode);
        if (currentIndex === -1) return false;

        const isChild = descendantNode.outside.length === 1;
        descendantNode.outside.splice(currentIndex, 1);

        return isChild;
      });
      currentNode.children = children;

      children.forEach((child) => {
        child.parent = currentNode;
        relationQueue.push(child);
      });
    }

    // 取出区域轮廓和洞
    const areaResult = [];
    const outerAreas = [];
    const innerAreas = []; // 内部环（但不是区域）
    const resultQueue = [...roots];
    while (resultQueue.length) {
      const currentNode = resultQueue.pop();
      if (!currentNode) break;

      if (!currentNode.clockwise) {
        // 逆时针是房间轮廓
        const clockwiseChildren = currentNode.children.filter((node) => node.clockwise);
        const holes = clockwiseChildren.map((node) => {
          node.isHole = true; // 标记为孔洞，不添加到innerAreas中
          return node.path;
        }); // 取开洞数据
        const holesEdges = clockwiseChildren.map((node) => node.edges); // 取开洞边

        const parentArea = null;
        const areaInfo: IAreaResult<T> = {
          path: currentNode.path,
          edges: currentNode.edges,
          holes,
          holesEdges,
          parent: parentArea,
          children: []
        };

        // 在父节点对应的区域childen里面添加当前区域
        let parentNode = currentNode.parent;
        while (parentNode) {
          if (parentNode.area) {
            // 添加父子关系
            parentNode.area.children.push(areaInfo);
            areaInfo.parent = parentNode.area;
            break;
          }
          parentNode = parentNode.parent;
        }

        currentNode.area = areaInfo;
        areaResult.push(areaInfo);
      } else {
        if (!currentNode.parent) {
          // 最外层轮廓
          outerAreas.push({
            // clockwise: currentNode.clockwise,`
            path: currentNode.path,
            edges: currentNode.edges,
            holes: [],
            holesEdges: [],
            parent: null,
            children: []
          });
        } else if (!currentNode.children.length && !currentNode.isHole) {
          innerAreas.push({
            clockwise: currentNode.clockwise,
            path: currentNode.path,
            edges: currentNode.edges
          });
        }
      }

      resultQueue.push(...currentNode.children);
    }

    // 剩余的edge
    const loopEdgeSet = new WeakSet<Edge<T>>();
    for (const loop of loops) {
      for (const edge of loop) {
        loopEdgeSet.add(edge);
      }
    }

    const remainingEdges = this._edges.filter((edge) => !loopEdgeSet.has(edge));
    remainingEdges.push(...this._oppositeEdges);
    for (const innerArea of innerAreas) {
      for (const edge of innerArea.edges) {
        remainingEdges.push(edge);
      }
    }

    return { areaResult, outerAreas, remainingEdges };
  }

  // 选下一条边（纯函数，DCEL 的 next）：在 currentEdge.to 处把所有出边按「环形角序」排序
  // （主键=出发精确切线角，次键=带符号曲率：右弯在前、直线居中、左弯在后），再取「反向入边方向」
  // 的前驱（紧贴其顺时针的一条）作为 next，即沿面左侧（内环逆时针）前进。
  // 用「反向入边方向」的虚拟 key 定位而非去找 twin 这条边：twin 存在时它恰落在该位置（取其前驱、自然排除自身）；
  // twin 不存在时（如墙体外轮廓只有单向边）也能取到正确前驱。构造即双射：每条出边恰是某入边的 next，
  // 所有半边因此分解为不相交的环（面）与开链（悬挂边），不会漏边。
  private findNextEdge(currentEdge: Edge<T>): Edge<T> | null {
    const toEdges = this._graph.get(currentEdge.to);
    if (!toEdges || toEdges.length === 0) return null;

    // 环形角序的排序键
    const angleOf = (e: Edge<T>) => e.angle[0];
    const curvOf = (e: Edge<T>) => edgeSignedCurvature(e);

    const sorted = [...toEdges].sort((a, b) => {
      if (!equalsNumber(angleOf(a), angleOf(b))) return angleOf(a) - angleOf(b);
      return curvOf(a) - curvOf(b);
    });

    // 反向入边方向的虚拟 key（= 若 twin 存在，twin 在排序中的位置）
    const revAngle = normalizeAngle(currentEdge.angle[1] + Math.PI);
    const revCurv = signedCurvatureAt(currentEdge.center, currentEdge.to, revAngle);
    const less = (e: Edge<T>) => {
      if (!equalsNumber(angleOf(e), revAngle)) return angleOf(e) < revAngle;
      return curvOf(e) < revCurv;
    };

    // 前驱：环序中 key 严格小于反向方向的最大者；若都不小于则环绕取最大（最后一条）
    let best: Edge<T> | null = null;
    for (const e of sorted) {
      if (less(e)) best = e;
    }
    return best ?? sorted[sorted.length - 1];
  }

  // 是否为「误差产生的退化弧环」——应过滤，不能算作闭合区域。
  // 判据不能只看面积：弧形凸起隔墙(如直墙上一段向上鼓起的弧)与直墙围出的房间面积可能很小(< 0.1㎡)，
  // 但它是真实房间，不该被丢弃。真正需要过滤的是「弧与近乎重合的弦围出的细缝」——
  // 其等效厚度(面积/周长)趋近于 0(浮点误差量级 ~1mm 内)，而真实房间即便面积小、等效厚度也可观。
  // 因此仅当「面积极小 且 等效厚度趋近于 0」时才判定为退化弧环。
  private isTinyArcLoop(edges: Edge<T>[]): boolean {
    const areaPath: IShapePoint2d[] = [];
    let hasArc = false;
    for (const edge of edges) {
      const shapePoint: IShapePoint2d = Point2dUtils.clone(edge.from);
      if (edge.center) {
        shapePoint.center = edge.center;
        shapePoint.clockwise = edge.clockwise;
        hasArc = true;
      }
      areaPath.push(shapePoint);
    }
    if (!hasArc) return false;
    const path = dispersePath(areaPath);
    const area = Math.abs(calculatePolygonArea(path)); // mm²
    const coreArea = area / (1000 * 1000); // ㎡

    // 周长(离散后的折线周长，弧已被采样成多段)
    let perimeter = 0;
    for (let i = 0; i < path.length; i++) {
      const a = path[i];
      const b = path[(i + 1) % path.length];
      perimeter += Math.hypot(b.x - a.x, b.y - a.y);
    }
    // 等效厚度：细缝→趋近 0；真实房间→可观(远大于浮点误差量级)
    const effectiveThickness = perimeter > 0 ? area / perimeter : 0;

    // 面积极小 且 等效厚度趋近于 0 才算退化弧环(细缝)。二者同时满足才过滤，
    // 保证真实的小面积弧形房间(等效厚度可观)不被误删。
    return coreArea <= 0.1 && effectiveThickness <= 2;
  }

  // 获取环：DCEL 半边面遍历。遍历每条未访问半边、沿 findNextEdge 前进，用 rho 检测取出环。
  // 每条半边恰属一个面，不会有边被遗弃；内环逆时针、最外轮廓顺时针，交由 getArea 分类。
  private getLoop() {
    // 直接以 this._edges（被交点打断后的一条条边）为半边集合：不合并、每条只用一次。
    const areas: Edge<T>[][] = [];
    const visited = new Set<Edge<T>>();

    for (const start of this._edges) {
      if (visited.has(start)) continue;

      const chain: Edge<T>[] = [];
      const indexInChain = new Map<Edge<T>, number>();
      let cur: Edge<T> | null = start;
      let cycleStart = -1;

      while (cur && !visited.has(cur)) {
        const existing = indexInChain.get(cur);
        if (existing !== undefined) {
          cycleStart = existing;
          break;
        }
        indexInChain.set(cur, chain.length);
        chain.push(cur);
        cur = this.findNextEdge(cur);
      }

      for (const e of chain) visited.add(e);

      if (cycleStart !== -1) {
        // 剔除悬挂刺/连接边（一出一回的反向 twin 对），得到干净的边界环
        const cycle = removeSpurs(chain.slice(cycleStart));
        if (cycle.length >= 2 && !this.isTinyArcLoop(cycle)) {
          areas.push(cycle);
        }
      }
    }

    return areas;
  }
}

// 对壁芯和区域轮廓进行对比，找出有效区域
export function compareCoreAndOutline<
  TCore extends { path: IPoint2d[]; children: TCore[]; parent: TCore | null; checked?: boolean },
  TOutline extends {
    path: IPoint2d[];
    children: TOutline[];
    parent: TOutline | null;
    checked?: boolean;
  }
>(
  coreAreaResult: TCore[],
  outlineAreaResult: TOutline[],
  isSeparateOutline: boolean = false
): {
  validCoreArea: TCore[];
  validOutlineArea: TOutline[];
  invalidOutlineArea: TOutline[];
} {
  const coreAreaLeaves = coreAreaResult.filter((coreArea) => !coreArea.children.length);
  const outlineAreaLeaves = outlineAreaResult.filter((outlineArea) => !outlineArea.children.length);

  const validCoreArea: TCore[] = [];
  const validOutlineArea: TOutline[] = [];
  const invalidOutlineArea: Set<TOutline> = new Set();

  // 从叶子节点开始查找
  const coreAreaQueue = [...coreAreaLeaves];

  while (coreAreaQueue.length) {
    const coreArea = coreAreaQueue.shift();
    if (!coreArea) break;

    coreArea.checked = true; // 标记为已处理，用于维持coreAreaQueue的顺序，使得后面的节点一定与前面的节点是包含/不相交关系

    // 查找内轮廓
    const outlineArea: TOutline[] = [];
    for (let i = 0; i < outlineAreaLeaves.length; i++) {
      let _outlineArea = outlineAreaLeaves[i];
      const relation = calculateShapeRelation(_outlineArea.path, coreArea.path, isSeparateOutline);

      // 轮廓不在壁芯区域内部/重叠，跳过
      if (!(relation === EClosedPathRelation.Inside || relation === EClosedPathRelation.Overlap)) continue;
      _outlineArea.checked = true;

      // 如果是在壁芯区域内部，还需要再检查下其父节点
      while (_outlineArea.parent) {
        const parentRelation = calculateShapeRelation(_outlineArea.parent.path, coreArea.path, isSeparateOutline);

        // 如果父节点不是在壁芯区域内部
        if (!(parentRelation === EClosedPathRelation.Inside || parentRelation === EClosedPathRelation.Overlap)) break;

        // 所有子元素标记为无效区域
        invalidOutlineArea.add(_outlineArea);
        _outlineArea = _outlineArea.parent;
        _outlineArea.checked = true;
      }

      outlineArea.push(_outlineArea);
    }

    // 找到唯一内轮廓，有效区域
    if (outlineArea.length === 1 || (outlineArea.length > 1 && outlineArea.every((area) => area === outlineArea[0]))) {
      // 找到唯一内轮廓
      validCoreArea.push(coreArea);
      validOutlineArea.push(outlineArea[0]);

      // 删除内轮廓
      const index = outlineAreaLeaves.findIndex((area) => area === outlineArea[0]);
      if (index !== -1) outlineAreaLeaves.splice(index, 1);

      // 添加父内轮廓
      if (outlineArea[0].parent && outlineArea[0].parent.children.every((child) => child.checked)) {
        outlineAreaLeaves.push(outlineArea[0].parent);
      }
    } else if (outlineArea.length > 1) {
      // 找到多个内轮廓，且不相同
      outlineArea.forEach((area) => {
        const index = outlineAreaLeaves.findIndex((_area) => _area === area);
        if (index !== -1) outlineAreaLeaves.splice(index, 1);

        invalidOutlineArea.add(area);

        // 添加父内轮廓
        if (area.parent && area.parent.children.every((child) => child.checked)) {
          outlineAreaLeaves.push(area.parent);
        }
      });
    }

    // 添加core父区域到队列
    if (coreArea.parent && coreArea.parent.children.every((child) => child.checked)) {
      coreAreaQueue.push(coreArea.parent);
    }
  }

  // 剩余的都是无效区域
  outlineAreaLeaves.forEach((node) => {
    invalidOutlineArea.add(node);

    let parentNode = node.parent;
    while (parentNode && !validOutlineArea.includes(parentNode)) {
      invalidOutlineArea.add(parentNode);
      parentNode = parentNode.parent;
    }
  });

  return {
    validCoreArea,
    validOutlineArea,
    invalidOutlineArea: Array.from(invalidOutlineArea.values())
  };
}

// function isOnOrInPolygon(polygon1: IPoint2d[], polygon2: IPoint2d[]) {
//   if (Curve2dUtils.isPolygonOnPolygon(polygon1, polygon2)) return true;
//   return Curve2dUtils.isPolygonInPolygon(polygon1, polygon2);
// }

function isOppositeEdge<T>(edge1: Edge<T>, edge2: Edge<T>) {
  if ((!isArcEdge(edge1) && isArcEdge(edge2)) || (isArcEdge(edge1) && !isArcEdge(edge2))) return false;

  const lineOppsite =
    edge1.from.x === edge2.to.x &&
    edge1.from.y === edge2.to.y &&
    edge1.to.x === edge2.from.x &&
    edge1.to.y === edge2.from.y;

  // 上面已排除一弧一直线，此处两者的弧性一致，任一不是弧即两者都不是
  if (!isArcEdge(edge1) || !isArcEdge(edge2)) return lineOppsite;

  return lineOppsite && Point2dUtils.equals(edge1.center, edge2.center) && edge1.clockwise === !edge2.clockwise;
}

function getTangentAngle(seg: IShapeSeg2d): [number, number] {
  if (seg instanceof LineSeg2d) {
    const angle = seg.getAngleWithXAxis();
    return [angle, angle];
  }
  if (seg instanceof ArcSeg2d) {
    return [seg.getTangentAngle(seg.start), seg.getTangentAngle(seg.end)];
  }
  return [0, 0];
}

function splitEdge<T>(edge: Edge<T>, startPos: number, endPos: number): Edge<T> | null {
  startPos = Math.max(0, Math.min(startPos, 1));
  endPos = Math.max(0, Math.min(endPos, 1));

  if (startPos >= endPos) return null;

  const seg = getShapeSeg(edge);
  const startPoint = getPointOnSeg(seg, startPos);
  const endPoint = getPointOnSeg(seg, endPos);

  if (!startPoint || !endPoint) return null;

  return {
    ...edge,
    from: startPoint,
    to: endPoint
  };
}

/** 边描述的是圆弧（带圆心）。收窄后 `center` 为必选，无需断言 */
function isArcEdge<TEdge extends IGeometryEdge>(edge: TEdge): edge is TEdge & { center: IPoint2d } {
  return !!edge.center;
}
